import * as R from 'ramda'
import * as MR from '@/libs/maybe-ramda'
import Maybe from 'data.maybe'
import * as types from '../mutation-types'
import moment from 'moment'
import { stateManager } from '../helper'
import { currency } from '@/libs/util'

const cradlePurchasesRepo = async () => {
  const { cradle } = (await import('@/container')).default
  return cradle.repository.purchasesRepository
}

const sm = stateManager(
  {
    purchaser: null,
    purchaserReceived: false
  },
  {
    entitlements: null,
    onPayment: false,
    offerings: null
  }
)

export default {
  namespaced: true,
  state: sm.get(),
  mutations: {
    [types.ENTITLEMENTS_SET] (state, obj) {
      state.entitlements = obj
    },
    [types.PURCHASER_SET] (state, obj) {
      state.purchaser = obj
      state.purchaserReceived = !!obj
    },
    [types.PURCHASES_RESET] (state) {
      sm.reset(state)
    },
    [types.ON_PAYMENT_SET] (state, value) {
      state.onPayment = value
    },
    [types.SET_OFFERINGS] (state, value) {
      state.offerings = value
    }
  },
  actions: {
    async init ({ commit }, { env }) {
      const purchasesRepo = await cradlePurchasesRepo()

      purchasesRepo.setDebugLogsEnabled(env === 'development')
      purchasesRepo.setup(REVENUECAT_API_KEY)
      purchasesRepo.getPurchaserInfo().then((purchaser) => {
        commit(types.PURCHASER_SET, purchaser)
      })
      purchasesRepo.getOfferings().then((offerings) => {
        commit(types.SET_OFFERINGS, offerings)
      })
    },
    async logIn ({ commit }, user) {
      const purchasesRepo = await cradlePurchasesRepo()

      const purchaser = await purchasesRepo.logIn(user)
      commit(types.PURCHASER_SET, purchaser)
    },
    async getOfferings ({ commit }) {
      const purchasesRepo = await cradlePurchasesRepo()

      const offerings = await purchasesRepo.getOfferings()
      commit(types.SET_OFFERINGS, offerings)
    },
    async purchase ({ commit }, { package: packagee, upgradeInfo }) {
      const purchasesRepo = await cradlePurchasesRepo()

      try {
        commit(types.ON_PAYMENT_SET, true)
        const { purchaserInfo } = await purchasesRepo.purchasePackage(
          packagee,
          { upgradeInfo }
        )

        commit(types.PURCHASER_SET, purchaserInfo)
      } catch (err) {
        if (err.userCancelled || err.error.code === 2) {
          // error code (2) has occured when a sandbox account changes subscription.
          return
        }

        const error = new Error(err)
        error.code = 'PAYMENT_DECLINED'
        throw error
      } finally {
        commit(types.ON_PAYMENT_SET, false)
      }
    },
    async reset ({ commit }) {
      const purchasesRepo = await cradlePurchasesRepo()

      await purchasesRepo.reset()
      commit(types.PURCHASER_SET, null)
    },
    getProductPlatform (_, id) {
      return id.includes('ios')
        ? 'ios'
        : id.includes('android')
          ? 'android'
          : null
    }
  },
  getters: {
    activeSubscriptions (state) {
      return MR.of(state.purchaser)
        .chain(MR.prop('activeSubscriptions'))
        .getOrElse([])
    },
    expStrDate (state) {
      return MR.of(state.purchaser)
        .chain(MR.prop('latestExpirationDate'))
        .map((iso) => moment(iso).format('ll'))
        .getOrElse('-')
    },
    hasPremium ({ purchaser }) {
      return R.compose(
        R.equals('premium'),
        R.path(['entitlements', 'active', 'premium', 'identifier'])
      )(purchaser)
    },
    hasMonthlyPremium (_, getters) {
      return R.any(R.includes('monthly'))(getters.activeSubscriptions)
    },
    hasAnnualPremium (_, getters) {
      return R.any(R.either(R.includes('yearly'), R.includes('annual')))(
        getters.activeSubscriptions
      )
    },
    hasDeprecatedPremium (_, getters) {
      const deprecated = ['com.readgraphy.products.monthly']
      return R.any(R.includes(R.__, deprecated))(getters.activeSubscriptions)
    },
    activeSubsPlatform (state, getters) {
      const id = R.last(getters.activeSubscriptions)
      const fallback = (_id) =>
        _id.includes('ios')
          ? 'ios'
          : _id.includes('android')
            ? 'android'
            : null
      const toPlatform = (store) =>
        store === 'app_store'
          ? Maybe.Just('ios')
          : store === 'play_store'
            ? Maybe.Just('android')
            : Maybe.Nothing()

      const platform = MR.of(state.purchaser)
        .chain(
          MR.path(['_response', 'subscriber', 'subscriptions', id, 'store'])
        )
        .chain(toPlatform)
        .getOrElse(null)

      return platform || fallback(id || '')
    },
    packages ({ offerings }) {
      const isValid = R.compose(
        (length) => length > 0,
        R.path(['current', 'availablePackages', 'length'])
      )

      if (!isValid(offerings)) {
        return null
      }

      const getMonthlyPrice = (p) =>
        p.currency_code && p.meta
          ? currency(p.currency_code, divideByMonths(p))
          : null
      const divideByMonths = (p) => {
        const val = p.price / p.meta.months
        return p.currency_code === 'KRW' ? Math.ceil(val) : val
      }

      const metadata = {
        annual: {
          product: {
            meta: { value: 'annual', months: 12 }
          }
        },
        monthly: {
          product: {
            meta: { value: 'monthly', months: 1 }
          }
        }
      }

      return R.pipe(
        R.path(['current', 'availablePackages']),
        R.groupBy((packagee) => packagee.packageType.toLowerCase()),
        R.mapObjIndexed(R.head),
        R.mergeDeepRight(metadata),
        R.mapObjIndexed(
          R.over(R.lensProp('product'), (p) =>
            R.assoc('monthlyPrice', getMonthlyPrice(p), p)
          )
        )
      )(offerings)
    },
    discountRate: (_state, { packages }, _rootState, { isBrowser }) => {
      const calc = (y, m) => Math.floor((1 - y / (m * 12)) * 100)

      return (
        (isBrowser
          ? '-'
          : calc(
            packages.annual.product.price,
            packages.monthly.product.price
          )) + '%'
      )
    }
  }
}
